import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Input } from '@/shared/components/ui/input';
import { Button } from '@/shared/components/ui/button';
import { Separator } from '@/shared/components/ui/separator';
import { AiBadge } from '@/shared/components/custom/ai-badge/AiBadge';
import { cn } from '@/core/lib/utils';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useAiGoalPanel, RETRYABLE_ERROR_KEY } from '../hooks/useAiGoalPanel';
import { AiProposedTaskList } from './AiProposedTaskList';
import { VoiceDictationButton } from './VoiceDictationButton';

export interface AiGoalPanelProps {
  projectId: string;
  boardId: string;
  onClose: () => void;
}

/**
 * Goal-to-task panel: goal → propose → editable list with per-row selection → apply.
 *
 * Owns the goal, the proposal, the edits and the selection — component state,
 * not Zustand, because nothing outside reads it and it dies with the panel.
 * Regenerating asks first, and here it can be dirty-aware: the panel owns the
 * state, so it confirms only when something was edited or deselected.
 */
export function AiGoalPanel({ projectId, boardId, onClose }: AiGoalPanelProps) {
  const { t } = useTranslation('ai');
  const {
    goal,
    setGoal,
    proposal,
    drafts,
    errorKey,
    proposeErrorKey,
    isProposePending,
    canPropose,
    voiceDictation,
    selection,
    hasTitleError,
    estimated,
    confirmOpen,
    confirmOptions,
    handleConfirm,
    handleCancel,
    handleGenerate,
    handleDraftChange,
    handleApply,
    runPropose,
    isApplyPending,
  } = useAiGoalPanel(projectId, boardId, onClose);

  return (
    <div data-testid="ai-goal-panel" className="flex max-h-[90dvh] flex-col">
      <div className="shrink-0 px-6 pt-6 pb-5">
        <AiBadge label={t('goalPanel.badge')} className="mb-3" />
        <h2 className="text-xl font-semibold text-foreground">{t('goalPanel.title')}</h2>

        {/* The goal and its action read as one control, the way the design draws it:
            one box, the button inside it, and the whole thing takes the focus ring. */}
        <div
          className={cn(
            // Sides spelled out rather than `p-1.5 pl-3`: the field inside runs at `px-0`, so
            // this padding is the only thing holding the text off the border, and it should not
            // depend on how the shorthand and the longhand happen to be ordered in the sheet.
            'mt-4 flex items-center gap-2 rounded-md border border-input bg-transparent',
            'py-1.5 pr-1.5 pl-3',
            'shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
          )}
        >
          {/* The mic sits inside the field, as the quick-create panel places it — beside it
              the button was stealing width from the text it dictates into. The right padding
              is what keeps a long goal from running under it. */}
          <div className="relative min-w-0 flex-1">
            <Input
              id="ai-goal-input"
              data-testid="ai-goal-input"
              aria-label={t('goalPanel.goalLabel')}
              placeholder={t('goalPanel.goalPlaceholder')}
              value={goal}
              disabled={voiceDictation.isProcessing}
              onChange={(e) => {
                voiceDictation.notifyManualEdit();
                setGoal(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canPropose) handleGenerate();
              }}
              // `dark:bg-transparent` too: Input carries `dark:bg-input/30`, which a plain
              // `bg-transparent` does not override (different variant), so in dark mode the
              // field painted its own surface inside this box — an input inside an input,
              // with the text flush against its left edge.
              className={cn(
                'h-8 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0',
                'dark:bg-transparent',
                voiceDictation.isSupported && 'pr-10',
              )}
            />
            {voiceDictation.isSupported && (
              <div className="absolute top-1/2 right-0 -translate-y-1/2">
                <VoiceDictationButton
                  state={voiceDictation.state}
                  onStart={voiceDictation.startRecording}
                  onStop={voiceDictation.stopRecording}
                  duration={voiceDictation.duration}
                />
              </div>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            className="shrink-0"
            data-testid="ai-goal-submit"
            onClick={handleGenerate}
            disabled={!canPropose}
          >
            {isProposePending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                {t('goalPanel.generating')}
              </>
            ) : (
              t('goalPanel.generate')
            )}
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-6">
        {errorKey && (
          <div
            role="alert"
            data-testid="ai-goal-error"
            className="mb-4 flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
          >
            <p>{t(errorKey)}</p>
            {proposeErrorKey === RETRYABLE_ERROR_KEY && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="self-start"
                data-testid="ai-goal-retry"
                onClick={() => runPropose(goal)}
              >
                {t('goalPanel.retry')}
              </Button>
            )}
          </div>
        )}

        {proposal && (
          <AiProposedTaskList
            proposal={proposal}
            drafts={drafts}
            selectedCount={selection.selectedCount}
            estimated={estimated}
            canRegenerate={canPropose}
            onRegenerate={handleGenerate}
            onDraftChange={handleDraftChange}
          />
        )}
      </div>

      {confirmOpen && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(value) => {
            if (!value) handleCancel();
          }}
          title={confirmOptions?.title ?? ''}
          description={confirmOptions?.description}
          confirmLabel={confirmOptions?.confirmLabel}
          cancelLabel={confirmOptions?.cancelLabel}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      <Separator className="shrink-0" />
      <div className="flex shrink-0 justify-end gap-2.5 px-6 py-4">
        <Button type="button" variant="outline" data-testid="ai-goal-cancel" onClick={onClose}>
          {t('goalPanel.cancel')}
        </Button>
        <Button
          type="button"
          data-testid="ai-goal-confirm"
          disabled={!proposal || selection.selectedCount === 0 || hasTitleError || isApplyPending}
          onClick={handleApply}
        >
          {isApplyPending
            ? t('quickCreate.confirmPending')
            : t('goalPanel.createTasks', { count: selection.selectedCount })}
        </Button>
      </div>
    </div>
  );
}
